// MAJLIS-62 (48b, D-22, A-3): the OTHER seats' voices, played on this phone.
//
// THE ONE RULE: a remote voice plays ONLY through a standard <audio> element whose srcObject is a MediaStream holding
// the remote WebRTC track. Never through an AudioContext, a gain node or any Web Audio graph, and never through
// livekit-client's own attach() (which, with webAudioMix on, keeps the element muted and routes the sound through Web
// Audio). The reason is echo: the browser's echo canceller takes its reference from the WebRTC playout path, so a
// remote voice on that path is subtracted from this phone's microphone and one routed through Web Audio is not. We
// transcribe every microphone, so a voice the canceller cannot see would come back as a phrase from THIS seat.
// The canceller is necessary and NOT sufficient: whether it keeps other people's voices out of a phone's microphone
// at full speaker volume is the owner's two-phone test (D-38, ROADMAP M2 row 3), unmeasured here.
//
// What this module decides, and nothing else:
// - one element per subscribed remote audio track, created when LiveKit subscribes and removed when it unsubscribes,
//   the connection ends or the seat leaves;
// - an element is muted while Listen is off (a nicety on top of the server's enforcement - the SERVER stops the audio
//   reaching this phone at all, MAJLIS-62's set_listen; this only makes the change instant here) and while D-38
//   silences the live voices on THIS phone during a replay (clip plus its tail, following the microphone gate, so it
//   ends on every path the gate ends on);
// - PLAYING NEEDS A TAP ON iOS (NOT MEASURED ON iOS): a browser may refuse play() without a user gesture. A refused
//   element is remembered and play() is tried again from inside the next tap anywhere in the room (`unlock`, called
//   by the app's document-level gesture listener). Consultant ruling 2 (2026-09-23, UI-C27 to be written): while
//   any voice is waiting, the app shows ONE line, "Tap to hear the room"; it is shown ONLY when a play() was
//   refused and goes on the first tap whose play() SUCCEEDS - `onWaitingChange` says when, from the play() promises
//   themselves, never from the tap alone. The retry is UNMEASURED ON iOS.
// - MAJLIS-64 (D-43(2), consultant ruling B): while a SPOKEN TRANSLATION plays on this phone, the live voices are
//   LOWERED to LIVE_UNDER - except on iOS, where `volume` is ignored by the browser (the hardware owns the level), so
//   there they are MUTED instead. No Web Audio gain as an exception: it would take the live voices off the echo
//   canceller's path (the one rule above). The lowering follows the microphone gate's `voice` hold, so it ends on
//   every path a spoken phrase ends on, as D-38's silence does for a replay; a replay's silence wins over it.
//   The person's own setting (UI-C21, kept in prefs.ts) can make it MUTED everywhere (`setMuteUnder`), for noisy places.
import { LIVE_UNDER } from "../constants";

/** The subset of HTMLAudioElement a voice needs (tests inject a fake; the app makes real ones). */
export interface VoiceElement {
  srcObject: MediaProvider | null;
  muted: boolean;
  /** 0..1; ignored by iOS Safari (always reads back 1 there), which is why iOS mutes instead (ruling B). */
  volume: number;
  autoplay: boolean;
  play(): Promise<void>;
  pause(): void;
  remove(): void;
}

export interface HearingOptions {
  /** Makes one <audio> element in the page (the app appends a hidden one to the document). */
  createElement: () => VoiceElement;
  /** new MediaStream([track]), injected (jsdom has no MediaStream). */
  streamOf: (track: MediaStreamTrack) => MediaStream;
  log?: (message: string, body?: unknown) => void;
  /** Ruling 2: told every time "some voice is waiting for a tap" changes (the app shows the tap prompt). */
  onWaitingChange?: (waiting: boolean) => void;
  /** MAJLIS-64 ruling B: iOS/iPadOS (detectIOS in the app), where lowering is muting. Absent: not iOS. */
  isIOS?: () => boolean;
}

/** What the LiveKit adapter reports; keyed by the publication's track sid. */
export interface RemoteAudioSink {
  add(key: string, track: MediaStreamTrack): void;
  remove(key: string): void;
  clear(): void;
}

export class Hearing implements RemoteAudioSink {
  private voices = new Map<string, VoiceElement>();
  private blocked = new Set<VoiceElement>();
  // Ruling 6 (DEPLOY CONDITION until T-118 passes): every seat joins not listening, so this starts off too; the
  // app sets it from the server's record.
  private listening = false;
  private silenced = false;
  private lowered = false;
  private muteUnder = false;
  private toldWaiting = false;

  constructor(private readonly opts: HearingOptions) {}

  /** LiveKit subscribed this seat to a remote audio track. The same key again (a resubscribe) replaces its element. */
  add(key: string, track: MediaStreamTrack): void {
    this.remove(key);
    const el = this.opts.createElement();
    el.autoplay = true;
    el.srcObject = this.opts.streamOf(track);
    this.voices.set(key, el);
    this.apply(el);
    this.start(el);
  }

  remove(key: string): void {
    const el = this.voices.get(key);
    if (!el) return;
    this.voices.delete(key);
    this.blocked.delete(el);
    this.tellWaiting();
    el.pause();
    el.srcObject = null;
    el.remove();
  }

  /** The connection ended or the seat left: every element goes. */
  clear(): void {
    for (const key of [...this.voices.keys()]) this.remove(key);
  }

  /** OUR record's Listen for this seat (seat_updated / the person's tap). */
  setListening(on: boolean): void {
    this.listening = on;
    for (const el of this.voices.values()) this.apply(el);
  }

  /** D-38: while THIS phone replays a phrase (and its tail), the live voices are silent here only. */
  setSilenced(on: boolean): void {
    this.silenced = on;
    for (const el of this.voices.values()) this.apply(el);
  }

  /** MAJLIS-64 (D-43(2), ruling B): while a spoken translation plays here, the live voices go under it - lowered to
   * LIVE_UNDER, or muted on iOS. Off restores full volume. */
  setLowered(on: boolean): void {
    this.lowered = on;
    for (const el of this.voices.values()) this.apply(el);
  }

  /** UI-C21 (D-43(2)): the person's setting - under a spoken translation the live voices are MUTED, not lowered. */
  setMuteUnder(on: boolean): void {
    this.muteUnder = on;
    for (const el of this.voices.values()) this.apply(el);
  }

  /** Called from inside a user gesture: try every element the browser refused to start. Each stays WAITING until
   * its play() resolves, so the tap prompt goes only on a tap that actually started the sound. */
  unlock(): void {
    if (this.blocked.size === 0) return;
    for (const el of [...this.blocked]) this.start(el);
  }

  /** How many remote voices are attached (tests and diagnostics). */
  count(): number {
    return this.voices.size;
  }

  /** Whether any voice is waiting for a tap before it can play. */
  waitingForTap(): boolean {
    return this.blocked.size > 0;
  }

  private apply(el: VoiceElement): void {
    const mute = this.lowered && (this.muteUnder || (this.opts.isIOS?.() ?? false));
    el.muted = !this.listening || this.silenced || mute;
    el.volume = this.lowered && !mute ? LIVE_UNDER : 1;
  }

  private start(el: VoiceElement): void {
    el.play().then(
      () => {
        if (this.blocked.delete(el)) this.tellWaiting();
      },
      (e: unknown) => {
        if (![...this.voices.values()].includes(el)) return; // removed meanwhile
        const name = e instanceof Error ? e.name : typeof e;
        this.blocked.add(el);
        this.tellWaiting();
        this.opts.log?.("live audio: the browser did not start a voice; waiting for a tap", { error: name });
      },
    );
  }

  private tellWaiting(): void {
    const waiting = this.blocked.size > 0;
    if (waiting === this.toldWaiting) return;
    this.toldWaiting = waiting;
    this.opts.onWaitingChange?.(waiting);
  }
}
